import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, of } from 'rxjs';
import { catchError, concatMap, distinctUntilChanged, filter, map, switchMap, withLatestFrom } from 'rxjs/operators';

import { AccountFacade } from '@mode/accounts/contract';
import { ChartFacade } from '@mode/charts/contract';
import { MetricClientService } from '@mode/shared/data-access-webapp';

import { MetricsHelpers } from '../..';
import { MetricFacade } from '../metric.facade';
import * as MetricActions from './metric.actions';

// eslint-disable-next-line @nx/enforce-module-boundaries
import { ChartSwitchActions } from '@mode/charts/domain';

@Injectable()
export class MetricEffects {
  loadMetrics$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(MetricActions.metricDialogLoadMetrics),
      withLatestFrom(this.accountFacade.currentOrg$),
      concatMap(([_, org]) => this.metricClient.getMetrics(org.username)),
      map((metrics) => MetricActions.metricDialogLoadMetricsSuccess({ metrics })),
      catchError((error) => of(MetricActions.metricDialogLoadMetricsFailure({ error })))
    );
  });

  loadMetric$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(MetricActions.metricQueryRouteLoadMetric),
      withLatestFrom(this.accountFacade.currentOrg$),
      concatMap(([action, org]) => this.metricClient.getMetric(org.username, action.metricToken)),
      map((metric) => MetricActions.metricQueryRouteLoadMetricSuccess({ metric })),
      catchError((error) => of(MetricActions.metricQueryRouteLoadMetricFailure({ error })))
    );
  });

  /**
   * ChartState:
   *
   * If there are unapplied changes then the chart is considered
   * to be in a "dirty" state; otherwise, all changes have been applied
   * and the state is "committed"
   */
  _chartState$ = createEffect(() => {
    return this.actions$.pipe(ofType(MetricActions.initChartStateIfUnset)).pipe(
      switchMap(({ token }) =>
        this.chartFacade.createUnappliedChangesFromVizToken(token).pipe(
          distinctUntilChanged(),
          map((changes): [string, number] => {
            return [token, changes];
          })
        )
      ),
      map(([token, changes]) =>
        MetricActions.setChartState({
          state: changes === 0 ? 'committed' : 'dirty',
          token,
        })
      )
    );
  });

  /**
   * Allowing Helix requests:
   *
   * Changes that don't result in SQL changes can
   * dispatch flamingo requests to select new data
   */
  _manuallyApplyChanges$ = createEffect(() => {
    return this.actions$.pipe(ofType(MetricActions.initChartStateIfUnset)).pipe(
      switchMap(({ token }) =>
        this.metricFacade.selectedMetric$.pipe(concatMap((metric) => this.onlyChartTokenWithUnchangedMetricSql$(token)))
      ),
      map((token) =>
        ChartSwitchActions.manuallyApplyChanges({
          token,
        })
      )
    );
  });

  /**
   * Allowing Chart persistence:
   *
   * Changes that don't result in SQL changes can
   * persist Chart changes via. the API
   *
   * This should be on a case by case basis
   */
  _setEphemeral$ = createEffect(() => {
    return this.actions$.pipe(ofType(MetricActions.initChartStateIfUnset)).pipe(
      switchMap(({ token }) =>
        this.actions$.pipe(
          ofType(ChartSwitchActions.updateChartNoEncoding, ChartSwitchActions.updateChart),
          filter((action) => action.token === token),
          switchMap(({ token }) => this.onlyChartTokenWithUnchangedMetricSql$(token))
        )
      ),
      map((token) =>
        ChartSwitchActions.setEphemeral({
          state: false,
          token,
        })
      )
    );
  });

  private onlyChartTokenWithUnchangedMetricSql$(vizToken: string): Observable<string> {
    return this.chartFacade.createEncodingFromVizToken(vizToken).pipe(
      withLatestFrom(this.metricFacade.selectedQueryRawSql$, this.metricFacade.selectedMetric$),
      filter(
        ([encoding, sql, metric]) =>
          !!metric &&
          !!sql &&
          !!encoding &&
          sql === MetricsHelpers.createDbtSqlFromSpec(MetricsHelpers.createMacroSpecFromEncoding(metric, encoding))
      ),
      map(() => vizToken)
    );
  }

  constructor(
    private actions$: Actions,
    private accountFacade: AccountFacade,
    private chartFacade: ChartFacade,
    private metricFacade: MetricFacade,
    private metricClient: MetricClientService
  ) {}
}
